import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, ShieldCheck, Compass, Smartphone, Globe2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { IMAGES, SITE } from "@/lib/data";

const HIGHLIGHTS = [
  { icon: GraduationCap, title: "Qualified Social Worker", text: "A professional foundation rooted in frontline social work practice." },
  { icon: Compass, title: "Founder & Principal Consultant", text: "Leading Epic Consultancy's partnership-driven approach to children's services." },
  { icon: ShieldCheck, title: "Safeguarding & Governance Specialist", text: "Expertise across safeguarding, governance, leadership and service transformation." },
  { icon: Smartphone, title: "Creator of Pocket Keyworker", text: "Bringing digital innovation to independence and wellbeing for young people." },
];

const MeetCharmaine = () => (
  <div data-testid="meet-charmaine-page">
    <section className="pt-40 pb-24 bg-cream">
      <div className="mx-auto max-w-7xl px-6 grid gap-14 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="overline mb-6">Meet Charmaine</p>
            <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy">
              Charmaine Laville
            </h1>
            <p className="mt-4 text-xl text-gold font-serif italic">Founder & Principal Consultant</p>
            <p className="mt-8 max-w-xl text-lg text-slate-600 leading-relaxed">
              Charmaine brings extensive experience leading and developing children's services,
              combining practical operational leadership with a genuine commitment to improving
              outcomes for children and young people.
            </p>
            <p className="mt-5 max-w-xl text-lg text-slate-600 leading-relaxed">
              As Director of Epic Care UK, she has developed real operational leadership experience —
              yet her focus through Epic Consultancy is partnership: designing, strengthening and
              transforming services alongside the organisations she works with.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] soft-shadow">
                <img
                  src={IMAGES.charmaine}
                  alt="Charmaine Laville"
                  className="h-[620px] w-full object-cover object-top"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="absolute -bottom-6 -left-6 rounded-2xl bg-navy px-6 py-5 text-cream soft-shadow"
              >
                <p className="font-serif text-lg">Epic Consultancy</p>
                <p className="text-xs tracking-[0.2em] uppercase text-gold mt-1">Children's Services</p>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-white border-y border-slate-200/70">
      <div className="mx-auto max-w-7xl px-6 py-24">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.06}>
              <div className="h-full rounded-3xl border border-slate-200 bg-cream p-8 transition-colors duration-300 hover:border-gold/60">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-gold">
                  <h.icon size={22} strokeWidth={1.6} />
                </div>
                <h3 className="font-serif text-xl text-navy">{h.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{h.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-6 py-28 text-center">
      <Reveal>
        <div className="mx-auto mb-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal text-cream">
          <Globe2 size={26} strokeWidth={1.6} />
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight">
          A Dominican heritage, an international commitment.
        </h2>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          Proud of her Dominican heritage, Charmaine is committed to supporting children's services
          across the Caribbean and internationally — sharing knowledge, building capacity and
          partnering with organisations working to improve the lives of children and young people.
        </p>
        <Button asChild className="mt-10 rounded-full bg-navy hover:bg-navy-light text-cream px-8 h-12">
          <Link to="/contact" data-testid="charmaine-cta">
            Start a Conversation <ArrowRight size={16} className="ml-2" />
          </Link>
        </Button>
      </Reveal>
    </section>
  </div>
);

export default MeetCharmaine;
